import { useEffect, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISS_KEY = "bios:install:dismissed";

export function InstallPrompt() {
  const [evt, setEvt] = useState<BeforeInstallPromptEvent | null>(null);
  const [canShowIosInstructions, setCanShowIosInstructions] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    function onBeforeInstall(e: Event) {
      e.preventDefault();
      setEvt(e as BeforeInstallPromptEvent);
    }
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const ua = window.navigator.userAgent;
    const isIos =
      /iphone|ipad|ipod/i.test(ua) || (ua.includes("Mac") && navigator.maxTouchPoints > 1);
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
    setCanShowIosInstructions(isIos && !isStandalone);

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const standalone =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches ||
      // iOS Safari
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true);
  if (dismissed || standalone || (!evt && !canShowIosInstructions)) return null;

  function dismiss() {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  }

  return (
    <div className="bios-install-banner" role="dialog" aria-label="Install Black Intelligence OS">
      <span>
        {evt ? "Install BIOS" : "Add BIOS to Home Screen: Share, then Add to Home Screen"}
      </span>
      {evt && (
        <button
          type="button"
          onClick={async () => {
            await evt.prompt();
            const choice = await evt.userChoice;
            setEvt(null);
            if (choice.outcome === "dismissed") dismiss();
          }}
        >
          Install
        </button>
      )}
      <button
        type="button"
        className="bios-install-dismiss"
        aria-label="Dismiss install prompt"
        onClick={dismiss}
      >
        ×
      </button>
    </div>
  );
}
